import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Landmark, CheckCircle2, Circle, Clock, ExternalLink, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { prepareExternal, openExternal, cancelExternal } from "@/lib/external";

const KYC_LABEL = {
  approved: "Identité vérifiée",
  pending: "Identité : vérifiée automatiquement par Stripe à l'étape suivante",
  rejected: "Identité : vérifiée par Stripe à l'étape suivante (aucune validation manuelle)",
  none: "Identité : vérifiée par Stripe à l'étape suivante (pièce d'identité + adresse)",
};

const Step = ({ ok, pending, label, hint, testId }) => (
  <li className="flex items-start gap-2 text-xs" data-testid={testId}>
    {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(160_84%_38%)]" />
      : pending ? <Clock className="h-4 w-4 shrink-0 text-[hsl(38_92%_40%)]" />
        : <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />}
    <div className="min-w-0">
      <p className={ok ? "text-foreground" : "text-muted-foreground"}>{label}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  </li>
);

/**
 * Parcours vendeur Stripe Connect : création du compte Express, vérification d'identité,
 * compte bancaire, puis retraits. Tout est réel (LIVE) — aucun état simulé.
 */
export const ConnectSection = ({ earningsAvailable = 0, onReady }) => {
  const [status, setStatus] = useState(null);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("FR");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connect/status");
      setStatus(data);
      if (onReady) onReady(data);
    } catch (e) {
      setStatus({ available: false, detail: apiError(e) });
    } finally {
      setLoading(false);
    }
  }, [onReady]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/connect/countries").then(({ data }) => {
      setCountries(data.countries || []);
      setCountry(data.default || "FR");
    }).catch(() => {});
  }, []);

  const startOnboarding = async () => {
    // Stripe refuse l'affichage en iframe : on réserve un onglet dès le clic si nécessaire
    const reserved = prepareExternal();
    setBusy(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin, country });
      if (data.available && data.onboarding_url) {
        const mode = openExternal(data.onboarding_url, reserved);
        if (mode === "tab") {
          toast.info("Stripe s'est ouvert dans un nouvel onglet. Terminez la vérification puis revenez ici et actualisez.");
          setBusy(false);
        }
        return;
      }
      cancelExternal(reserved);
      toast.info(data.detail || "Stripe Connect indisponible pour le moment.");
    } catch (e) {
      cancelExternal(reserved);
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    const reserved = prepareExternal() || window.open("", "_blank");
    setBusy(true);
    try {
      const { data } = await api.post("/connect/dashboard");
      if (reserved && !reserved.closed) reserved.location.href = data.url;
      else window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      cancelExternal(reserved);
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Skeleton className="h-28 rounded-xl" data-testid="connect-loading" />;

  const s = status || {};
  // La vérification d'identité Stripe (onboarding Connect terminé) suffit : aucune double validation.
  const kycOk = s.kyc_status === "approved" || !!s.onboarded;
  const minW = s.min_withdraw ?? 15;
  const enoughEarnings = Number(earningsAvailable) >= minW;
  const due = s.requirements_due || [];
  const pendingVerif = s.pending_verification || [];

  return (
    <div className="rounded-xl border border-border p-3 space-y-3" data-testid="connect-section">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Versements réels via Stripe Connect</p>
        <button onClick={load} className="text-muted-foreground hover:text-foreground" aria-label="Actualiser" data-testid="connect-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <ul className="space-y-1.5" data-testid="connect-checklist">
        <Step ok={kycOk} label={kycOk ? KYC_LABEL.approved : (KYC_LABEL[s.kyc_status] || KYC_LABEL.none)} hint={kycOk && s.onboarded ? "Validée par Stripe (pièce d'identité + adresse)" : undefined} testId="connect-step-kyc" />
        <Step ok={!!s.has_account} label={s.has_account ? `Compte Stripe créé${s.country ? ` (${s.country})` : ""}` : "Créer votre compte Stripe (2 minutes)"} testId="connect-step-account" />
        <Step ok={!!s.onboarded} pending={!!s.has_account && !s.onboarded}
          label={s.onboarded ? "Identité et compte bancaire validés par Stripe" : "Vérification d'identité et compte bancaire (IBAN) chez Stripe"}
          hint={!s.onboarded && s.has_account ? (
            due.length ? `${due.length} information(s) manquante(s) — reprenez la vérification` :
              pendingVerif.length ? "Stripe vérifie vos informations, cela prend en général quelques minutes" :
                s.disabled_reason ? `Statut Stripe : ${s.disabled_reason}` : undefined
          ) : (s.bank_account ? `${s.bank_account.bank_name || "Banque"} ••••${s.bank_account.last4} (${s.bank_account.currency})` : undefined)}
          testId="connect-step-stripe" />
        <Step ok={enoughEarnings} label={`Revenus disponibles ≥ ${minW} €`} hint={!enoughEarnings ? "Vos ventes (80 % du prix) alimentent vos revenus disponibles" : undefined} testId="connect-step-earnings" />
      </ul>

      {s.available === false && (
        <p className="text-[11px] text-[hsl(38_92%_40%)] flex items-start gap-1.5" data-testid="connect-unavailable">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {s.detail || "Stripe Connect indisponible pour le moment."}
        </p>
      )}

      {!s.has_account && s.available !== false && (
        <div className="space-y-2">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="rounded-xl h-10" data-testid="connect-country-select">
              <SelectValue placeholder="Pays de votre compte bancaire" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="w-full rounded-xl" onClick={startOnboarding} disabled={busy} data-testid="connect-onboard-button">
            <Landmark className="h-4 w-4 mr-1" /> {busy ? "Redirection vers Stripe…" : "Créer mon compte Stripe et vérifier mon identité"}
          </Button>
          <p className="text-[11px] text-muted-foreground">Vous serez redirigé vers Stripe (page sécurisée) : pièce d'identité, adresse et IBAN. Vos revenus sont ensuite versés sur votre compte bancaire.</p>
        </div>
      )}

      {s.has_account && !s.onboarded && s.available !== false && (
        <Button size="sm" variant="outline" className="w-full rounded-xl" onClick={startOnboarding} disabled={busy} data-testid="connect-resume-button">
          <Landmark className="h-4 w-4 mr-1" /> {busy ? "Redirection vers Stripe…" : "Reprendre la vérification Stripe"}
        </Button>
      )}

      {s.has_account && (s.onboarded || s.details_submitted) && (
        <Button size="sm" variant="ghost" className="w-full rounded-xl text-xs" onClick={openDashboard} disabled={busy} data-testid="connect-dashboard-button">
          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ouvrir mon espace Stripe (virements bancaires, IBAN)
        </Button>
      )}
    </div>
  );
};

export const connectCanWithdraw = (status, earningsAvailable) =>
  !!status && status.onboarded && Number(earningsAvailable) >= (status.min_withdraw ?? 15);
